import React, { useState, type RefObject } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { NavBar } from './NavBar';
import { StatsRow } from './StatsRow';
import { heroSocials, heroStatement, heroStats } from '../data/heroStats';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileHeroSweep } from './MobileHeroSweep';
import { QuestDragReveal } from './QuestDragReveal';
import { EASE_SOFT, INTRO, focusIn, logoFocus } from '../utils/reveal';

/** Stacked logo at the top of the hero */
const LOGO = 'https://cdn.magicpatterns.com/uploads/judzNcXwimifLQYqjxq8CM/Vector_(5).svg';
/** Wide one-line wordmark, only used huge and faint in the background */
const WORDMARK = 'https://cdn.magicpatterns.com/uploads/c59J3HuuyLAaZJUH7snjBF/Vector_(3).svg';
interface HeroSectionProps {
  /** The drone anchors itself to the middle of this section */
  sectionRef: RefObject<HTMLElement>;
}

/** Statement top left, logo centered, stats along the bottom; the drone floats in the middle. */
export function HeroSection({
  sectionRef
}: HeroSectionProps) {
  const reduce = useReducedMotion();
  // Both the logo and the background wordmark load from the web: their intros start once they've arrived
  const [logoReady, setLogoReady] = useState(false);
  const [markReady, setMarkReady] = useState(false);
  const logoIntro = logoFocus(INTRO.logo, reduce);
  // Phones: the intro is one diagonal sweep over the whole hero (see MobileHeroSweep)
  const mobile = useIsMobile();
  return (
    // Mobile: fills the whole screen; desktop: 80% of the window height
    <section ref={sectionRef} data-drone-fixed="true" className="relative flex h-[100svh] min-h-[560px] w-full flex-col overflow-hidden bg-white text-black md:h-[80vh] md:min-h-[600px]" data-id="element-115" data-mp="3847dtuuj3h2u">
      <NavBar data-id="element-116" data-mp="333a0pwodld87" />
      {/* Phones: the whole hero is revealed in one diagonal sweep, bottom-left → top-right */}
      {mobile && !reduce && <MobileHeroSweep data-id="element-117" data-mp="18rlk7q2hx2lp" />}

      {/* Centered logo, top — matches the reference at 1720px: ~107px tall, ~20px from the top */}
      {/* Mobile: slightly smaller, centred at the top with the contact pill below it */}
      {/* Desktop: vertically centred on the contact button (and its label) — the button's top offset
          (1.6 × vw) plus half its height — so logo and nav share one centre line */}
      {/* Desktop: pinned to the far left, mirroring the contact button's inset on the right */}
      <div data-drone-push className="absolute left-6 top-6 z-30 md:left-[calc(1.6*var(--vw))] md:top-[calc(1.6*var(--vw)+clamp(2.5rem,calc(3.2*var(--vw)),3rem)/2)] md:translate-x-0 md:-translate-y-1/2" data-id="element-118" data-mp="3rsyzcbduqer1">
        {/* key: swap animation variant cleanly if the layout crosses the breakpoint */}
        {/* The intro waits for the logo file: it loads from the web, so the focus-in used to finish
            before the image arrived and the logo simply popped in */}
        {/* Quest: shard three hides behind the logo — concealed, nothing gives it away until it's dragged aside */}
        <QuestDragReveal spot="logo" reveal={44} shardSize={32} concealed data-id="element-119" data-mp="23u6cvpg54vh6">
          <motion.a key={mobile ? 'm' : 'd'} href="#" className="block" {...mobile ? {} : {
            ...logoIntro,
            animate: logoReady ? logoIntro.animate : logoIntro.initial
          }} data-id="element-120" data-mp="23s1xobcska1l">
            <img src={LOGO} onLoad={() => setLogoReady(true)} alt="Home"
            // Two-line stacked logo (~2.4:1): about the contact button's height, so ~110px wide at 1440px
            className="h-[2.5rem] w-auto md:h-[clamp(1.9rem,calc(2.7*var(--vw)),2.5rem)]" data-id="element-121" data-mp="1pv6kq7tf5yg9" />
          </motion.a>
        </QuestDragReveal>
      </div>

      {/* Mobile: agency tagline centred beneath the contact buttons (where the socials used to sit) */}
      <p data-drone-push className="absolute inset-x-0 bottom-14 z-30 overflow-hidden px-3 pb-[0.12em] text-center text-base font-bold uppercase leading-tight tracking-[0.01em] text-black md:hidden" data-id="element-122" data-mp="368n3436j11q0">
        <motion.span className="block" data-id="element-123" data-mp="2l1mcmnnx19tb">
          {heroStats[0]}
        </motion.span>
      </p>

      {/* Mobile: social links stacked vertically in the top-right corner, level with the logo */}
      <nav aria-label="Social" data-drone-push className="absolute right-6 top-6 z-30 md:hidden" data-id="element-124" data-mp="2c30yd4ph8gyc">
        <ul className="flex flex-col items-end gap-1 text-right text-base font-bold uppercase leading-tight tracking-[0.01em] text-black" data-id="element-125" data-mp="2h23shu3x52rj">
          {heroSocials.map(link => <li key={link.label} data-id="element-126" data-mp="38srsv3xrq445">
              <a href={link.href} target="_blank" rel="noopener noreferrer" className="rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" data-id="element-127" data-mp="38qymlpr2hlwo">
                {link.label}
              </a>
            </li>)}
        </ul>
      </nav>

      {/* Top-left block — proportions from the reference at 1720px: ~27px statement, ~54px pill */}
      {/* Hidden visually on mobile (the logo sits here instead); kept for screen readers */}
      {/* Desktop: shifted right of the logo (about a fifth of the width in), still left-aligned — not centred */}
      <div className="sr-only md:not-sr-only md:absolute md:left-[22%] md:top-[calc(1.6*var(--vw))] md:z-30" data-id="element-128" data-mp="fe55jy1c6r8m">
        <h1 className="text-[clamp(1.25rem,calc(1.6*var(--vw)),1.9rem)] font-semibold leading-[1.12] tracking-[-0.045em]" data-id="element-129" data-mp="agwvkkqcdtig">
          {/* Each line comes into focus in turn (and can be knocked about by a hypnotised Pixel) */}
          {heroStatement.map((line, li) => <motion.span key={line} data-drone-push className="block w-fit" {...focusIn(INTRO.statement + li * INTRO.line, reduce)} data-id="element-130" data-mp="3sumk3iibdjte" data-mpi={li}>
              {line}
            </motion.span>)}
        </h1>
      </div>

      {/* Giant faint logo behind Pixel — desktop: as large as the hero allows, centred (the stacked logo is
          fairly tall, so it's fitted to the height rather than stretched edge to edge) */}
      {/* (Wide wordmark: stretched edge to edge, vertically centred) */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 hidden -translate-y-1/2 px-[1.28vw] md:block" aria-hidden="true" data-id="element-131" data-mp="1f6kbaeq8inck">
        {/* Slowly rises from nothing to its faint 1.5% once the file has loaded */}
        <motion.img src={WORDMARK} alt="" className="block h-auto w-full" onLoad={() => setMarkReady(true)} initial={reduce ? false : {
          opacity: 0
        }} animate={{
          opacity: reduce || markReady ? 0.015 : 0
        }} transition={{
          duration: 2.4,
          ease: EASE_SOFT,
          delay: INTRO.wordmark
        }} data-id="element-132" data-mp="1p46vngqaz73x" />
      </div>
      {/* Phones: turned vertical, centred and running most of the hero's height */}
      <motion.img src={WORDMARK} alt="" aria-hidden="true" onLoad={() => setMarkReady(true)} initial={reduce ? false : {
        opacity: 0
      }} animate={{
        opacity: reduce || markReady ? 0.03 : 0
      }} transition={{
        duration: 2.4,
        ease: EASE_SOFT,
        delay: INTRO.wordmark
      }} className="pointer-events-none absolute left-1/2 top-1/2 z-10 block h-auto w-[calc(max(100svh,560px)-16px)] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 md:hidden" data-id="element-133" data-mp="8rjd02dqlt2t" />

      {/* Proof points along the bottom, just above the showreel */}
      <div className="absolute inset-x-0 bottom-14 z-30 md:bottom-[clamp(1rem,calc(2*var(--vw)),2rem)]" data-id="element-134" data-mp="3nh1k6nmy9m4p">
        <StatsRow data-id="element-135" data-mp="3jf6mdt489qvo" />
      </div>

      {/* The drone and its chat live in a page-wide layer (DroneCompanion) anchored to this section */}
    </section>
  );
}